import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { auth0Audience } from '../config';
import { getUserInfo } from '../services/user';
import { cleanupUserData, setDefaultAuthorizationHeader } from '../services';
import { IUser } from '../types/User.interface';

const useAuthenticatedUser = (): [IUser | null, boolean] => {
    const [user, setUser] = useState<IUser | null>(null);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const token = await getAccessTokenSilently({
                    authorizationParams: { audience: auth0Audience },
                });

                const userData = await getUserInfo(token);
                setDefaultAuthorizationHeader(token); // set the default token if user is found
                setUser({ ...userData, token });
            } catch (err) {
                console.error('Failed to fetch user data:', err);
                setUser(null);
                cleanupUserData();
                await logout({ logoutParams: { returnTo: window.location.origin } });
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchUser();
        } else if (!isLoading) {
            // it means the user is guest or not authenticated
            setUser(null);
            setLoading(false);
        }
    }, [getAccessTokenSilently, isAuthenticated, isLoading, logout]);

    return [user, loading];
};

export default useAuthenticatedUser;
